import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const BASE = import.meta.env.VITE_API_BASE || "/api";

// 2 hours of no mouse/keyboard/touch activity logs the user out — a periodic soft-reset for
// sessions that otherwise never close (the point-of-sale machine just stays logged in for days).
const IDLE_LOGOUT_MS = 2 * 60 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  // Сканер регистрирует здесь «есть несохранённый скан» — автовыход по простою его не выбрасывает.
  const idleLogoutBlockerRef = useRef(null);
  const setIdleLogoutBlocker = useCallback((fn) => { idleLogoutBlockerRef.current = fn; }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    axios.get(`${BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setUser(r.data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  // A 401 from ANY request (not just /auth/me above) means the token expired or was revoked —
  // without this, the app used to just sit there with a live `user` but every subsequent request
  // silently failing (nothing loads, no obvious way out short of a manual reinstall/relaunch —
  // this is the exact bug reported from Уточкина on 2026-08-24). /auth/login itself also 401s on
  // a wrong password, but at that point `user` is already null and no token is stored yet, so
  // clearing both here is a harmless no-op for that case — the LoginPage's own .catch still gets
  // the rejected promise and shows "Неверный логин или пароль" as before.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (username, password, location) => {
    const r = await axios.post(`${BASE}/auth/login`, { username, password, location });
    localStorage.setItem("token", r.data.token);
    setUser(r.data.user);
    // Best-effort — a fresh login is a good moment to catch up with the central server, but
    // shouldn't block the login itself or surface an error if we're offline right now. Central
    // has no SERVER_URL to sync against — the backend 400s every time, so skip it entirely there.
    if (!r.data.user.isCentral) {
      axios.post(`${BASE}/sync`, {}, { headers: { Authorization: `Bearer ${r.data.token}` } }).catch(() => {});
    }
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  // Idle auto-logout — only runs while logged in. Activity listeners just stamp a ref (no
  // re-render per mousemove); a once-a-minute interval is what actually checks and logs out.
  useEffect(() => {
    if (!user) return;
    lastActivityRef.current = Date.now();
    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "touchstart"];
    events.forEach(e => document.addEventListener(e, markActive));
    const interval = setInterval(() => {
      if (idleLogoutBlockerRef.current?.()) return;
      if (Date.now() - lastActivityRef.current >= IDLE_LOGOUT_MS) logout();
    }, IDLE_CHECK_INTERVAL_MS);
    return () => {
      events.forEach(e => document.removeEventListener(e, markActive));
      clearInterval(interval);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setIdleLogoutBlocker }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
